import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, RowClickedEvent } from 'ag-grid-community';
import PageHeader from '../../../components/common/PageHeader';
import PageTransition from '../../../components/common/PageTransition';
import ReactionCard from '../../../components/reactions/ReactionCard';
import { ReactionEditorModal } from '../../../components/chemistry/ReactionEditor';
import { useUIStore } from '../../../stores/uiStore';
import ExportButton from '../../../components/export/ExportButton';
import type { ReactionListItem } from '../../../types/reactions';
import { formatDate } from '../../../utils/dateFormat';

type ViewMode = 'cards' | 'table';

const ReactionsHubPage: React.FC = () => {
  const navigate = useNavigate();
  const openUploadDialog = useUIStore((s) => s.openUploadDialog);

  // Pop-out context: when opened from CompoundDetailPanel's ReactionsTab, the URL carries
  // ?smiles=&project_id=&popout=true so the page lands pre-filtered to the compound.
  const popoutParams = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      smiles: p.get('smiles') || '',
      projectId: p.get('project_id'),
    };
  }, []);

  // Filters (UI state only)
  const [search, setSearch] = useState(popoutParams.smiles);
  const [roleFilter, setRoleFilter] = useState<'any' | 'REACTANT' | 'PRODUCT'>('any');
  const [yieldMin, setYieldMin] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('any');
  const [hasCollisions, setHasCollisions] = useState(false);
  const [hasMatched, setHasMatched] = useState(false);
  const [view, setView] = useState<ViewMode>('cards');
  const [reactionEditorOpen, setReactionEditorOpen] = useState(false);
  const [reactions, setReactions] = useState<ReactionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userProjects, setUserProjects] = useState<{ id: number }[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchReactions = async () => {
      setLoading(true);
      try {
        const { default: apiClient } = await import('../../../api/client');
        // Fetch projects list first, then reactions from each project
        const projRes = await apiClient.get('/projects');
        const projects: { id: number }[] = projRes.data || [];

        if (!cancelled) setUserProjects(projects);
        const allItems: ReactionListItem[] = [];
        const projectsToScan = popoutParams.projectId
          ? projects.filter((p) => p.id === Number(popoutParams.projectId))
          : projects;
        for (const proj of projectsToScan) {
          try {
            const res = await apiClient.get(`/projects/${proj.id}/reactions?limit=200`);
            if (res.data?.items) {
              allItems.push(...res.data.items.map((item: any) => ({
                ...item,
                file: item.file || { id: 0, name: 'Unknown' },
              })));
            }
          } catch {
            // skip project if it errors
          }
        }

        // Also fetch reactions with no project (project_id IS NULL) via old endpoint.
        // Skipped in pop-out mode — caller scoped us to one project.
        try {
          if (popoutParams.projectId) throw new Error('skip-no-project-fallback');
          const oldRes = await apiClient.get('/reactions?limit=200');
          if (oldRes.data?.reactions) {
            for (const r of oldRes.data.reactions) {
              // Only add if not already present (from project fetch)
              if (!allItems.find(item => item.id === r.id)) {
                allItems.push({
                  id: r.id,
                  reaction_smiles: r.reaction_smiles,
                  rxnfile_available: false,
                  source: 'MANUAL_UPLOAD' as const,
                  source_ref: null,
                  created_at: r.created_at,
                  file: { id: r.file_id || 0, name: r.file_name || 'Unknown' },
                  conditions: { solvents: null, catalysts: null, temperature_c: null, time_h: null },
                  outcome: { yield_percent: null, yield_type: null },
                  counts: { reactants: 0, products: 0, catalysts: 0 },
                  enrichment_rollup: {
                    matched_participants: 0, total_participants: 0,
                    matched_products: 0, total_products: 0,
                    patent_collisions: { total: 0, by_zone: {} },
                    knowledge_centre: { in_surechembl_products: 0, in_chembl_products: 0, in_pubchem_products: 0 },
                    biology: { targets: 0, pathways: 0 },
                  },
                  reaction_type: null,
                  thumbnail: { kind: 'placeholder', url: '' },
                });
              }
            }
          }
        } catch {
          // old endpoint may not exist
        }

        // Sort by ID descending (newest first)
        allItems.sort((a, b) => b.id - a.id);

        if (!cancelled) setReactions(allItems);
      } catch {
        // silent fail — empty list
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchReactions();
    return () => { cancelled = true; };
  }, [refreshKey, popoutParams.projectId]);

  const [duplicateWarning, setDuplicateWarning] = useState<{ existingId: number; message: string } | null>(null);

  const handleSaveNewReaction = async (rxnfile: string, smiles: string) => {
    try {
      const { default: apiClient } = await import('../../../api/client');
      const targetProjectId = userProjects.length > 0 ? userProjects[0].id : 1;
      const res = await apiClient.post('/reactions', {
        project_id: targetProjectId,
        reaction_smiles: smiles || null,
        rxnfile: rxnfile || null,
      });
      if (res.data?.duplicate) {
        setReactionEditorOpen(false);
        setDuplicateWarning({
          existingId: res.data.existing_id,
          message: res.data.message || 'This reaction already exists.',
        });
        return;
      }
      if (res.data?.id) {
        navigate(`/chemistry/reactions/${res.data.id}`);
      }
    } catch (err) {
      console.error('[ReactionsHub] Failed to save reaction:', err);
    }
  };

  const filtered = useMemo(() => {
    let list = reactions;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          (r.reaction_smiles || '').toLowerCase().includes(q) ||
          (r.file?.name || '').toLowerCase().includes(q) ||
          (r.source_ref || '').toLowerCase().includes(q) ||
          (r.reaction_type || '').toLowerCase().includes(q)
      );
    }
    if (sourceFilter !== 'any') {
      list = list.filter((r) => r.source === sourceFilter);
    }
    if (yieldMin) {
      const min = parseFloat(yieldMin);
      if (!isNaN(min)) list = list.filter((r) => (r.outcome.yield_percent ?? 0) >= min);
    }
    if (hasCollisions) {
      list = list.filter((r) => r.enrichment_rollup.patent_collisions.total > 0);
    }
    if (hasMatched) {
      list = list.filter((r) => r.enrichment_rollup.matched_participants > 0);
    }
    return list;
    // roleFilter is captured but filtering by role requires participant-level data (future API)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactions, search, roleFilter, sourceFilter, yieldMin, hasCollisions, hasMatched]);

  const goToDetail = (id: number) => navigate(`/chemistry/reactions/${id}`);

  // AG Grid columns
  const columnDefs: ColDef<ReactionListItem>[] = useMemo(
    () => [
      {
        headerName: '',
        field: 'id',
        width: 70,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const r = params.data as ReactionListItem;
          const sig = r.counts.reactants || r.counts.products
            ? `${r.counts.reactants}\u2192${r.counts.products}`
            : '?';
          return (
            <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center text-xs font-bold text-gray-400">
              {sig}
            </div>
          );
        },
      },
      {
        headerName: 'Signature',
        valueGetter: (p) => {
          const r = p.data!;
          const parts = [`${r.counts.reactants} \u2192 ${r.counts.products}`];
          if (r.counts.catalysts) parts.push('+ catalyst');
          return parts.join(' ');
        },
        flex: 1,
        minWidth: 130,
      },
      {
        headerName: 'Type',
        field: 'reaction_type',
        width: 160,
        cellRenderer: (p: any) => {
          if (!p.value) return <span className="text-gray-400">&mdash;</span>;
          return (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
              {p.value}
            </span>
          );
        },
      },
      {
        headerName: 'Yield %',
        field: 'outcome.yield_percent' as any,
        valueGetter: (p) => p.data?.outcome.yield_percent,
        width: 90,
        cellRenderer: (p: any) => {
          const v = p.value;
          if (v == null) return <span className="text-gray-400">&mdash;</span>;
          return <span className="font-semibold text-emerald-700">{v}%</span>;
        },
      },
      {
        headerName: 'Solvent',
        valueGetter: (p) => p.data?.conditions.solvents?.join(', ') ?? '\u2014',
        width: 120,
      },
      {
        headerName: 'Temp (\u00b0C)',
        valueGetter: (p) => p.data?.conditions.temperature_c,
        width: 90,
        cellRenderer: (p: any) => p.value != null ? `${p.value}` : '\u2014',
      },
      {
        headerName: 'Time (h)',
        valueGetter: (p) => p.data?.conditions.time_h,
        width: 85,
        cellRenderer: (p: any) => p.value != null ? `${p.value}` : '\u2014',
      },
      {
        headerName: 'Matched',
        valueGetter: (p) => {
          const er = p.data?.enrichment_rollup;
          return er && er.total_participants > 0
            ? `${er.matched_participants}/${er.total_participants}`
            : '\u2014';
        },
        width: 90,
      },
      {
        headerName: 'Collisions',
        valueGetter: (p) => p.data?.enrichment_rollup.patent_collisions.total ?? 0,
        width: 100,
        cellRenderer: (p: any) => {
          if (!p.value) return <span className="text-gray-400">0</span>;
          return <span className="text-red-600 font-semibold">{p.value}</span>;
        },
      },
      {
        headerName: 'Source',
        field: 'source',
        width: 120,
        cellRenderer: (p: any) => (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
            {(p.value || '').replace('_', ' ')}
          </span>
        ),
      },
      {
        headerName: 'Created',
        field: 'created_at',
        width: 110,
        valueFormatter: (p) => p.value ? formatDate(p.value) : '',
      },
    ],
    []
  );

  return (
    <PageTransition>
      <div className="flex flex-col h-full min-h-screen" style={{ background: 'var(--content-bg)' }}>
        <PageHeader
          title="Reactions"
          subtitle="Browse and manage chemical reactions across your projects"
          breadcrumbs={[
            { label: 'Chemistry', to: '/chemistry/files' },
            { label: 'Reactions' },
          ]}
          actions={
            <div className="flex items-center gap-2">
              <button
                onClick={() => setReactionEditorOpen(true)}
                className="px-5 py-2.5 bg-white text-teal-600 text-sm font-semibold rounded-xl hover:bg-teal-50 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Draw Reaction
              </button>
              <button
                onClick={() => openUploadDialog()}
                className="px-5 py-2.5 bg-white text-emerald-600 text-sm font-semibold rounded-xl hover:bg-emerald-50 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Import Reactions
              </button>
              <ExportButton
                dataType="reactions"
                totalCount={filtered.length}
                reactionIds={filtered.map(r => r.id)}
              />
            </div>
          }
        />

        {/* Filter row + view toggle */}
        <div className="px-6 pt-4 pb-2 max-w-7xl mx-auto w-full">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-3 flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <input
                type="text"
                placeholder="Search reactions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 w-full text-sm bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all placeholder-gray-400"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Role filter */}
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
              className="px-3 py-2 text-sm bg-gray-50 rounded-lg border-0 focus:ring-2 focus:ring-emerald-500"
            >
              <option value="any">Any role</option>
              <option value="REACTANT">Reactant</option>
              <option value="PRODUCT">Product</option>
            </select>

            {/* Yield min */}
            <input
              type="number"
              placeholder="Yield min %"
              value={yieldMin}
              onChange={(e) => setYieldMin(e.target.value)}
              className="w-28 px-3 py-2 text-sm bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder-gray-400"
            />

            {/* Source filter */}
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3 py-2 text-sm bg-gray-50 rounded-lg border-0 focus:ring-2 focus:ring-emerald-500"
            >
              <option value="any">Any source</option>
              <option value="MANUAL_UPLOAD">Manual Upload</option>
              <option value="ORD">ORD</option>
              <option value="SURF">SURF</option>
              <option value="INDIGO_ELN">Indigo ELN</option>
            </select>

            {/* Toggles */}
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hasCollisions}
                onChange={(e) => setHasCollisions(e.target.checked)}
                className="accent-red-500 w-3.5 h-3.5"
              />
              Collisions
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hasMatched}
                onChange={(e) => setHasMatched(e.target.checked)}
                className="accent-emerald-500 w-3.5 h-3.5"
              />
              Matched
            </label>

            {/* Spacer */}
            <div className="flex-1" />

            {/* View toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setView('cards')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  view === 'cards' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="3" width="7" height="7" rx="1" strokeWidth={2} />
                  <rect x="14" y="3" width="7" height="7" rx="1" strokeWidth={2} />
                  <rect x="3" y="14" width="7" height="7" rx="1" strokeWidth={2} />
                  <rect x="14" y="14" width="7" height="7" rx="1" strokeWidth={2} />
                </svg>
                Cards
              </button>
              <button
                onClick={() => setView('table')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  view === 'table' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                Table
              </button>
            </div>

            {/* Count */}
            <span className="text-sm text-gray-500">
              <span className="font-bold text-gray-800">{filtered.length}</span> reactions
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 pb-6 max-w-7xl mx-auto w-full">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin mb-4" />
              <p className="text-sm text-gray-400">Loading reactions...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-20 h-20 mb-6 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl flex items-center justify-center">
                <svg className="w-10 h-10 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 12h16m-6-6l6 6-6 6m-8-6a1 1 0 100-2 1 1 0 000 2z" />
                </svg>
              </div>
              <p className="text-xl font-semibold text-gray-700">No reactions yet</p>
              <p className="text-gray-400 mt-2 text-center max-w-md">
                Import reaction files (RXN, RDF) or draw a reaction to begin.
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setReactionEditorOpen(true)}
                  className="px-5 py-2.5 bg-white text-teal-600 text-sm font-semibold rounded-xl border border-teal-200 hover:bg-teal-50 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Draw Reaction
                </button>
                <button
                  onClick={() => openUploadDialog()}
                  className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-600 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Import Reactions
                </button>
              </div>
            </div>
          ) : view === 'cards' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-3">
              {filtered.map((r) => (
                <ReactionCard key={r.id} reaction={r} onClick={goToDetail} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden mt-3 ag-theme-alpine" style={{ height: 'calc(100vh - 320px)' }}>
              <AgGridReact<ReactionListItem>
                rowData={filtered}
                columnDefs={columnDefs}
                defaultColDef={{ sortable: true, resizable: true }}
                getRowId={(p) => String(p.data.id)}
                rowHeight={70}
                headerHeight={40}
                animateRows
                rowClass="hover:bg-gray-50 cursor-pointer"
                onRowClicked={(e: RowClickedEvent<ReactionListItem>) => {
                  if (e.data) goToDetail(e.data.id);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <ReactionEditorModal
        isOpen={reactionEditorOpen}
        onClose={() => setReactionEditorOpen(false)}
        onSave={handleSaveNewReaction}
        title="Draw New Reaction"
      />

      {/* UploadDialog replaced by UnifiedImportDialog in AppLayout */}

      {/* Duplicate reaction warning */}
      {duplicateWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Duplicate Reaction</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">{duplicateWarning.message}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDuplicateWarning(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={() => {
                  navigate(`/chemistry/reactions/${duplicateWarning.existingId}`);
                  setDuplicateWarning(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors"
              >
                View Existing
              </button>
            </div>
          </div>
        </div>
      )}
    </PageTransition>
  );
};

export default ReactionsHubPage;
